import React from "react";
import Container from "react-bootstrap/esm/Container";
import UsersTable from "../../components/tables/usersTable";
import { ColumnDef, FilterFn } from "@tanstack/react-table";
import { fuzzySort } from "../../components/tables/fuzzySort";
import {
  Fisher,
  listOrganizationFishers,
} from "../../api/listOrganizationFishers";
import { Link } from "react-router-dom";

const globalSearch: FilterFn<any> = (row, _columnId, filterValue) => {
  const q = String(filterValue ?? '').toLowerCase().trim()
  if (!q) return true

  return (
    row.original.displayName?.toLowerCase().includes(q) ||
    row.original.profile.organizationCodeNumber?.toLowerCase().includes(q) ||
    row.original.commercialLicense?.toLowerCase().includes(q)
  )
}

export default function AdminAllFishers() {
  const columns = React.useMemo<ColumnDef<Fisher, any>[]>(
    () => [
      {
        accessorFn: (row: any) => {
          return { displayName: row.displayName, userID: row.userID };
        },
        id: "displayName",
        header: "Name",
        cell: (info) => {
          return (
            <strong>
              <Link to={`/fisher/${info.getValue().userID}`} title="Click to open Fisher Profile" target="_blank" rel="noopener noreferrer">
                {info.getValue().displayName}
              </Link>
            </strong>
          );
        },
        footer: (props) => props.column.id,
        filterFn: "fuzzy",
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.displayName ?? "";
          const b = rowB.original.displayName ?? "";
          const compare = a.localeCompare(b);
          return compare;
        },
      },
      //{
      //  accessorFn: (row) => {
      //    return {
      //      userName: row.userName,
      //      lastLogin: row.lastLogin ? new Date(row.lastLogin).toLocaleString('en-US', {
      //        year: 'numeric',
      //        month: 'long',
      //        day: 'numeric',
      //        hour: 'numeric',
      //        minute: 'numeric',
      //      }) : "User has never logged in" };
      //  },
      //  id: "userName",
      //  header: "Login",
      //  cell: (info) => {
      //    return (
      //      <span title={`${info.getValue().lastLogin}`}>
      //        {info.getValue().userName}
      //      </span>
      //    );
      //  },
      //  footer: (props) => props.column.id,
      //  filterFn: "fuzzy",
      //  sortingFn: fuzzySort,
      //},
      //{
      //  accessorFn: (row) => {
      //    return {
      //      status: row.status,
      //      lastLogin: row.lastLogin ? "Last login: " + new Date(row.lastLogin).toLocaleString('en-US', {
      //        year: 'numeric',
      //        month: 'long',
      //        day: 'numeric',
      //        hour: 'numeric',
      //        minute: 'numeric',
      //      }) : "User has never logged in"
      //    };
      //  },
      //  id: "status",
      //  header: "Account",
      //  cell: (info) => {
      //    return (
      //      <span title={`${info.getValue().lastLogin}`}>
      //        {info.getValue().status}
      //      </span>
      //    );
      //  },
      //  footer: (props) => props.column.id,
      //  filterFn: "fuzzy",
      //  sortingFn: fuzzySort,
      //},
      {
        accessorFn: (row) => {
          return {
            code: row.profile.organizationCodeNumber,
            lastLogin: row.lastLogin ? "Last login: " + new Date(row.lastLogin).toLocaleString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: 'numeric',
              minute: 'numeric',
            }) : "User has never logged in"
          };
        },
        id: "code",
        header: "Code",
        cell: (info) => {
          return (
            <span title={`${info.getValue().lastLogin}`}>
              {info.getValue().code}
            </span>
          );
        },
        footer: (props) => props.column.id,
        filterFn: "fuzzy",
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.profile.organizationCodeNumber ?? "";
          const b = rowB.original.profile.organizationCodeNumber ?? "";
          const compare = a.localeCompare(b);
          return compare;
        },
      },
      {
        accessorFn: (row) => row.email,
        id: "email",
        header: "Email",
        cell: (info) => {
          return (
            <Link to={`mailto://${info.getValue()}`} title="Click to draft a new email">
              {info.getValue()}
            </Link>
          );
        },
        footer: (props) => props.column.id,
        filterFn: "fuzzy",
        sortingFn: fuzzySort,
      },
      {
        accessorFn: (row) => row.phone,
        id: "phone",
        header: "Phone",
        cell: (info) => {
          return (
            <Link to={`tel://${info.getValue()}`} title="Click to call">
              {info.getValue()}
            </Link>
          );
        },
        footer: (props) => props.column.id,
        filterFn: "fuzzy",
        sortingFn: fuzzySort,
      },
      {
        accessorFn: (row) => {
          var detail = "Permanent";
          if (row.commercialActiveDate && row.commercialExpireDate)
            detail = new Date(row.commercialActiveDate).toLocaleDateString('en-US') + ' to ' + new Date(row.commercialExpireDate).toLocaleDateString('en-US');
          else if (row.commercialExpireDate)
            detail = 'Expires ' + new Date(row.commercialExpireDate).toLocaleDateString('en-US');
          else if (row.commercialActiveDate)
            detail = 'Active ' + new Date(row.commercialActiveDate).toLocaleDateString('en-US');

          return { license: row.commercialLicense, detail: detail }
        },
        id: "commercial",
        header: "License",
        cell: (info) => {
          return (
            <span title={`${info.getValue().detail}`}>
              {info.getValue().license}
            </span>
          );
        },
        footer: (props) => props.column.id,
        filterFn: "fuzzy",
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.commercialLicense ?? "";
          const b = rowB.original.commercialLicense ?? "";
          const compare = a.localeCompare(b);
          return compare;
        },
      },
      {
        accessorFn: (row) => {
          var lastExpired = "Never Licensed";
          if (row.commercialExpireDate && row.commercialActive === true)
            lastExpired = 'Expires: ' + new Date(row.commercialExpireDate).toLocaleDateString('en-US');
          else if (row.commercialExpireDate && row.commercialActive === false)
            lastExpired = 'Expired: ' + new Date(row.commercialExpireDate).toLocaleDateString('en-US');
          else if (row.commercialActive)
            lastExpired = 'Does not expire';
          
          return { status: row.licenseStatus, lastExpired: lastExpired }
        },
        id: "licenseStatus",
        header: "Status",
        cell: (info) => {
          return (
            <span title={`${info.getValue().lastExpired}`}>
              {info.getValue().status}
            </span>
          );
        },
        footer: (props) => props.column.id,
        filterFn: "fuzzy",
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.licenseStatus ?? "";
          const b = rowB.original.licenseStatus ?? "";
          const compare = a.localeCompare(b);
          return compare;
        },
      },
      //{
      //  accessorFn: (row) => row.subsistenceLicense,
      //  id: "subsistence",
      //  header: "Subsistence",
      //  cell: (info) => info.getValue(),
      //  footer: (props) => props.column.id,
      //  filterFn: "fuzzy",
      //  sortingFn: fuzzySort,
      //},
    ],
    []
  );

  return (
    <div className="py-5">
      <Container fluid>
        <h1 className="text-primary mb-5">Fishers</h1>
        <UsersTable
          columns={columns}
          includeCORA={false}
          globalSearchFn={globalSearch}
          lookupList={listOrganizationFishers}
          role={"fisher"}
        />
      </Container>
    </div>
  );
}
